import { useState, useEffect, useCallback, useRef } from "react";
import { apiGet } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { Card, PageHeader, DataTable, Badge } from "../components/dc";
import { AccessibleModal } from "../components/AccessibleModal";

// Friendly label + badge tone per email kind.
const KINDS = {
  submission_notification: { label: "Submission", tone: "cyan" },
  approval_notification: { label: "Approval", tone: "green" },
  approval_nudge: { label: "Approval nudge", tone: "green" },
  invoice_reminder: { label: "Invoice reminder", tone: "amber" },
  price_stale_reminder: { label: "Stale price", tone: "amber" },
  price_drift_alert: { label: "Price drift", tone: "pink" },
  lpo_reminder: { label: "LPO reminder", tone: "amber" },
  unknown: { label: "Other", tone: "muted" },
};
const kindLabel = (k) => (KINDS[k] || { label: k || "Other" }).label;
const kindTone = (k) => (KINDS[k] || { tone: "muted" }).tone;
const STATUS_TONE = { sent: "green", failed: "red", skipped: "amber" };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("en-GB"); } catch { return iso || "—"; } };

export default function EmailLog() {
  usePageTitle("Email Log");
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);   // full entry incl html
  const [loadingOne, setLoadingOne] = useState(false);
  const requestRef = useRef(null);

  const load = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController(); requestRef.current = controller;
    const params = { limit: 200 };
    if (kind !== "all") params.kind = kind;
    if (status !== "all") params.status = status;
    try { const result = await apiGet("/email-log", { params, signal: controller.signal }); if (!controller.signal.aborted) setData(result); }
    catch { if (!controller.signal.aborted) setData({ emails: [], kinds: [], statuses: [] }); }
  }, [kind, status]);
  useEffect(() => { load(); return () => requestRef.current?.abort(); }, [load]);

  const openEntry = async (row) => {
    setLoadingOne(true);
    try { setSelected(await apiGet(`/email-log/${row._id}`)); }
    catch { setSelected({ ...row, html: "<p style='font-family:sans-serif;padding:20px'>Could not load the email body.</p>" }); }
    finally { setLoadingOne(false); }
  };

  const columns = [
    { key: "ts", label: "Time", width: "1.1fr", render: (r) => <span style={{ color: "var(--color-text-2)" }}>{fmtTime(r.ts)}</span> },
    { key: "kind", label: "Type", width: "0.9fr", render: (r) => <Badge tone={kindTone(r.kind)}>{kindLabel(r.kind)}</Badge> },
    { key: "to", label: "To", width: "1.3fr", render: (r) => <span title={r.to} style={{ fontSize: 13 }}>{r.to || "—"}</span> },
    { key: "subject", label: "Subject", width: "2fr", render: (r) => <span title={r.subject} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}>{r.subject || "—"}</span> },
    { key: "status", label: "Status", width: "0.7fr", align: "right", render: (r) => <Badge tone={STATUS_TONE[r.status] || "muted"}>{r.status}</Badge> },
  ];

  const sel = { background: "var(--color-bg-panel, #0f0a1e)", color: "var(--color-text-1)", border: "1px solid var(--hairline)", borderRadius: 9, padding: "9px 12px", fontSize: 13.5 };
  const rows = data?.emails || [];
  const failed = rows.filter((r) => r.status === "failed").length;
  const skipped = rows.filter((r) => r.status === "skipped").length;

  return (
    <div data-testid="email-log">
      <PageHeader title="Email Log"
        subtitle="Email delivery history. Credential-bearing action links are removed from staff previews." />

      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <select value={kind} onChange={(e) => setKind(e.target.value)} style={sel} data-testid="email-kind-filter">
          <option value="all">All types</option>
          {(data?.kinds || []).map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={sel} data-testid="email-status-filter">
          <option value="all">All statuses</option>
          {(data?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <span style={{ fontSize: 12.5, color: "var(--color-text-3)" }}>
          {data ? `${data.total ?? rows.length} total` : "Loading…"}
          {failed > 0 && <span style={{ color: "#fb7185", fontWeight: 600 }}> · {failed} failed</span>}
          {skipped > 0 && <span style={{ color: "#fbbf24", fontWeight: 600 }}> · {skipped} skipped</span>}
        </span>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        perPage={15}
        searchKeys={["to", "subject", "kind"]}
        searchPlaceholder="Search recipient or subject…"
        onRowClick={openEntry}
        emptyText="No emails logged yet. Entries appear here as the app sends mail."
      />

      {selected && (
        <AccessibleModal title="Email preview" testId="email-preview-modal" onClose={() => setSelected(null)} width={820}>
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--hairline)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                  <Badge tone={kindTone(selected.kind)}>{kindLabel(selected.kind)}</Badge>
                  <Badge tone={STATUS_TONE[selected.status] || "muted"}>{selected.status}</Badge>
                  <span style={{ fontSize: 12, color: "var(--color-text-3)" }}>{fmtTime(selected.ts)}</span>
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selected.subject}</div>
                <div style={{ fontSize: 12.5, color: "var(--color-text-3)", marginTop: 2 }}>
                  To {selected.to}{selected.resendId ? ` · Resend ${selected.resendId}` : ""}{selected.error ? ` · ${selected.error}` : ""}
                </div>
              </div>
              <button data-testid="email-preview-close" onClick={() => setSelected(null)} style={{ background: "none", border: "1px solid var(--hairline)", borderRadius: 8, color: "var(--color-text-1)", padding: "6px 11px", cursor: "pointer", fontSize: 13, flexShrink: 0 }}>Close</button>
            </div>
            <div style={{ background: "#fff", flex: 1, overflow: "auto" }}>
              {loadingOne ? <div style={{ padding: 40, color: "#555", fontFamily: "sans-serif" }}>Loading…</div> : (
                <iframe data-testid="email-preview-body" title="Email body" sandbox="allow-same-origin"
                  onLoad={event => {
                    // Scripts, forms, popups and top navigation stay sandboxed.
                    // Same-origin DOM access is only used for keyboard dismissal.
                    event.currentTarget.contentDocument?.addEventListener("keydown", key => { if (key.key === "Escape") { key.preventDefault(); setSelected(null); } });
                  }}
                  srcDoc={`<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'">${selected.html || "<p style='font-family:sans-serif;padding:20px'>No body stored.</p>"}`}
                  style={{ width: "100%", height: "60vh", border: "none", display: "block" }} />
              )}
            </div>
          </div>
        </AccessibleModal>
      )}
    </div>
  );
}
